import React, { useState, useEffect } from 'react';
import { getFeedStats, FeedStats, getCompletedImports, FeedImportStatus } from '../services/api';
import './DashboardPage.css';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981'];

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<FeedStats | null>(null);
  const [completedImports, setCompletedImports] = useState<FeedImportStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 10000); // Aggiorna ogni 10 secondi
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const [statsData, importsData] = await Promise.all([
        getFeedStats(),
        getCompletedImports(),
      ]);
      setStats(statsData);
      // Limita a ultimi 20 import e ordina per data decrescente
      setCompletedImports(
        importsData
          .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
          .slice(0, 20)
      );
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!stats) {
    return <div className="error">Nessun dato disponibile</div>;
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('it-IT').format(num);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#10b981';
      case 'failed':
        return '#ef4444';
      case 'pending':
        return '#f59e0b';
      case 'downloading':
      case 'processing':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      completed: '✓ Completato',
      failed: '✗ Fallito',
      pending: '⏱ In attesa',
      downloading: '⬇ Download',
      processing: '⚙ Elaborazione',
    };
    return labels[status] || status;
  };

  return (
    <div className="dashboard-page">
      <div className="container">
        <h2>📊 Dashboard Statistiche</h2>

        <div className="stats-grid">
          <div className="stat-card card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <div className="stat-value">
                {formatNumber(stats.totalProducts || 0)}
              </div>
              <div className="stat-label">Prodotti Totali</div>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <div className="stat-value">{stats.activeImports || 0}</div>
              <div className="stat-label">Importazioni Attive</div>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <div className="stat-value">{stats.failedImports || 0}</div>
              <div className="stat-label">Importazioni Fallite</div>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-value">
                {formatNumber(stats.byFeedType?.BOOTSTRAP || 0)}
              </div>
              <div className="stat-label">Bootstrap</div>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-value">
                {formatNumber(stats.byFeedType?.DAILY || 0)}
              </div>
              <div className="stat-label">Daily</div>
            </div>
          </div>

          <div className="stat-card card">
            <div className="stat-icon">⏰</div>
            <div className="stat-content">
              <div className="stat-value">
                {formatNumber(stats.byFeedType?.HOURLY || 0)}
              </div>
              <div className="stat-label">Hourly</div>
            </div>
          </div>
        </div>

        <div className="recent-imports card">
          <h3>📋 Storico Importazioni (Ultimi 20)</h3>
          {completedImports.length > 0 ? (
            <div className="imports-table-wrapper">
              <table className="imports-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Categoria</th>
                    <th>Durata</th>
                    <th>Processati</th>
                    <th>Falliti</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {completedImports.map((imp) => (
                    <tr key={imp.id}>
                      <td>{formatDate(imp.startTime)}</td>
                      <td>
                        <span className="badge badge-type">{imp.feedType}</span>
                      </td>
                      <td>{imp.category}</td>
                      <td>{formatDuration(imp.startTime, imp.endTime)}</td>
                      <td className="text-right">{formatNumber(imp.processedItems || 0)}</td>
                      <td className="text-right">
                        {imp.failedItems > 0 ? (
                          <span className="text-error">{formatNumber(imp.failedItems)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(imp.status) }}
                        >
                          {getStatusLabel(imp.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">Nessuna importazione completata</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
